<template>
  <BasePaneCard
    heading="Sentimentos das conversas"
    :isLoading="isLoading"
  >
    <div class="conversation-feelings">
      <template v-if="!dataset?.error">
        <div
          v-for="(value, key) in feelings"
          :key="key"
          class="conversation-feelings__item"
          :class="[
            `conversation-feelings__item--${key}`,
            {
              'conversation-feelings__item--disabled': isAppliedPageFilter(['feeling']) && !isAppliedPageFilter(['feeling'], [key])
            }
          ]"
          v-tooltip="{
            theme: 'tooltip-top-right',
            content: 'Abrir lista<br>de conversas.'
          }"
          @click.prevent="openLeadConversations(key)"
        >
          <div class="conversation-feelings__item__details">
            <h4>
              {{ key | feeling | capitalize }}
            </h4>

            <p>
              <template v-if="!isAppliedPageFilter(['feeling']) || isAppliedPageFilter(['feeling'], [key])">
                {{ value | percentage }}
              </template>

              <template v-else>
                -
              </template>
            </p>
          </div>
        </div>
      </template>

      <BaseEmptyState v-else>
        <SvgIconRobot/>

        <h3>
          Ops! Algo deu errado...
        </h3>

        <p>
          <a
            href="javascript:;"
            @click.prevent="fetchData()"
          >
            Tentar novamente
          </a>
        </p>
      </BaseEmptyState>

      <CommonLeadConversations
        :isOpen="isOpenLeadConversations"
        :heading="leadConversationsHeading"
        :urlParams="leadConversations.urlParams"
      />
    </div>
  </BasePaneCard>
</template>

<script>
import { dateFilter, percentageFilter, feelingFilter, capitalizeFilter } from '@/utils/filters'
import BasePaneCard from '@/components/base/BasePaneCard'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import CommonLeadConversations from '@/components/common/CommonLeadConversations'
import { mapState, mapGetters, mapMutations } from 'vuex'
import axios from 'axios'
import dayjs from 'dayjs'

export default {
  name: 'WidgetConversationFeelings',

  components: {
    BasePaneCard,
    BaseEmptyState,
    CommonLeadConversations
  },

  filters: {
    date: dateFilter,
    percentage: percentageFilter,
    feeling: feelingFilter,
    capitalize: capitalizeFilter
  },

  data () {
    return {
      isLoading: false,
      dataset: null,
      isOpenLeadConversations: false,
      leadConversations: {
        filter: 'sentiment',
        key: '',
        urlParams: ''
      }
    }
  },

  computed: {
    ...mapState('base', [
      'mainApi',
      'pageFilter'
    ]),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    ...mapGetters('base', ['isAppliedPageFilter']),

    feelings () {
      const totalMessages = this.dataset?.valid_sentiments_customer_messages | 0

      let feeling = {
        negative: 0,
        neutral: 0,
        positive: 0
      }

      if (totalMessages) {
        const {
          negative_sentiment: negative,
          neutral_sentiment: neutral,
          positive_sentiment: positive

        } = this.dataset

        feeling = {
          negative: (negative * 100) / totalMessages,
          neutral: (neutral * 100) / totalMessages,
          positive: (positive * 100) / totalMessages
        }
      }

      return feeling
    },

    leadConversationsHeading () {
      const { key } = this.leadConversations

      return key
        ? `Sentimento ${feelingFilter(key)}`
        : ''
    }
  },

  watch: {
    pageFilter () {
      this.dataset = null
      this.fetchData()
    }
  },

  mounted () {
    this.$root.$on('pane-tray-close', this.closeLeadConversations)

    this.fetchData()
  },

  methods: {
    ...mapMutations('smartSummary', {
      setFeelings: 'SET_FEELINGS'
    }),

    getIcon (key) {
      switch (key) {
        case 'negative':
          return 'SvgIconFaceSad'

        case 'neutral':
          return 'SvgIconFaceNeutral'

        case 'positive':
          return 'SvgIconFaceHappy'

        default:
          return ''
      }
    },

    getFetchDataParams (leadConversationsMode) {
      let params = ''
      let [dateStart, dateEnd] = this.pageFilter.dateRange
      const { action, csat, feeling, status } = this.pageFilter

      if (!dateStart || !dateEnd) {
        dateStart = dayjs()
        dateEnd = dateStart.add(1, 'day')
      }

      if (leadConversationsMode) {
        params += '?'

        params += `&filter=${this.leadConversations.filter}`
        params += `&key=${this.leadConversations.key}`

        params += `&firstDate=${dateFilter(dateStart, 'base')}`
        params += `&lastDate=${dateFilter(dateEnd, 'base')}`

        if (action.length) {
          params += `&actions=${action.join()}`
        }
      } else {
        params += `/${dateFilter(dateStart, 'base')}/${dateFilter(dateEnd, 'base')}`

        params += '?'

        if (action.length) {
          params += `&actionId=${action.join()}`
        }
      }

      if (csat.length) {
        params += `&csat=${csat.join()}`
      }

      if (feeling.length) {
        params += `&sentiments=${feeling.join()}`
      }

      if (status.length) {
        params += `&status=${status.join()}`
      }

      return params
        .replace(/\?$/, '')
        .replace(/\?&/, '?')
    },

    async fetchData () {
      this.isLoading = true

      try {
        const params = this.getFetchDataParams()
        const url = `${this.mainApi}integration/app/get-sentiment-platform${params}`
        const response = await axios.get(url)
        const hasResponseError = typeof response.data === 'string'

        this.dataset = hasResponseError ? null : response.data

        this.setFeelings(this.feelings)
      } catch (error) {
        this.dataset = { error }

        throw error
      } finally {
        this.isLoading = false
      }
    },

    updateLeadConversations (key) {
      this.leadConversations.key = key
      this.leadConversations.urlParams = this.getFetchDataParams(true)
    },

    openLeadConversations (key) {
      this.$root.$emit('pane-tray-close', 'leadConversationsContext')

      this.updateLeadConversations(key)

      this.isOpenLeadConversations = true
    },

    closeLeadConversations (context) {
      if (context === 'leadConversationsContext') {
        this.isOpenLeadConversations = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.conversation-feelings {
  display: flex;
  height: 100%;
  gap: 15px;
  justify-content: center;
  align-items: center;
  margin-top: 10px;

  &__item {
    flex-grow: 1;
    display: flex;
    gap: 10px;
    flex-direction: column;
    justify-content: center;
    padding: 20px;
    align-items: center;
    border-radius: 16px;
    transition: all 0.1s;
    cursor: pointer;

    &::before {
      $size: 45px;

      content: '';
      display: block;
      width: $size;
      height: $size;
      border-radius: 100%;
      background: {
        position: 50% 50%;
        repeat: no-repeat;
        size: 100% auto;
      }
    }

    &:hover {
      background: rgba(nth($colors-custom, 2), 0.5);
    }

    &--negative {
      &::before {
        background-image: url('~@/assets/img/main/icons/icon-face-sad-component.svg');
      }
    }

    &--neutral {
      &::before {
        background-image: url('~@/assets/img/main/icons/icon-face-neutral-component.svg');
      }
    }

    &--positive {
      &::before {
        background-image: url('~@/assets/img/main/icons/icon-face-happy-component.svg');
      }
    }

    &--disabled {
      &::before,
      h4,
      p {
        @extend %disabled;
      }
    }

    &__details {
      text-align: center;

      h4 {
        font: {
          weight: 400;
          size: 14px;
        }
        line-height: 100%;
      }

      p {
        font: {
          weight: 700;
          size: 14px;
        }
        line-height: 160%;
      }
    }
  }
}
</style>
